/**
 * Firestore ユーザードキュメント生成
 * Firebase SDK に依存しない。serverTimestamp を引数で受け取ることで
 * hosting (firebase/firestore) と landingpage (firebase/firestore) の
 * どちらからでも利用可能。
 */

import type { RegistrationFormData, RegistrationSource } from './types';
import {
  DEFAULT_APP_MODE,
  DEFAULT_NOTIFICATION_SETTINGS,
  DEFAULT_PET_NAME,
} from './constants';

/**
 * 登録フォームデータから Firestore ユーザードキュメントを生成する
 *
 * @param data - 登録フォームの入力データ
 * @param userId - Firebase Auth UID
 * @param serverTimestamp - 各アプリの serverTimestamp() 関数
 * @param source - 登録元識別子
 * @returns Firestore に書き込むプレーンオブジェクト
 */
export function buildUserDocument<TTimestamp>(
  data: RegistrationFormData,
  userId: string,
  serverTimestamp: () => TTimestamp,
  source: RegistrationSource,
): Record<string, unknown> {
  const ts = serverTimestamp();

  // ベースドキュメント（全ユーザー共通）
  // petName は登録時には収集しない（端末到着後にマイページで設定）
  const doc: Record<string, unknown> = {
    userId,
    email: data.email,
    displayName: data.displayName,
    petName: DEFAULT_PET_NAME,
    appMode: DEFAULT_APP_MODE,
    accountCreatedTime: ts,
    lastLoginTime: ts,
    notificationSettings: { ...DEFAULT_NOTIFICATION_SETTINGS },
    fcmToken: '',
    isDonor: data.isDonor,
    hasAppliedDevice: data.wantsDevice,
    points: 0,
    subscriptionStatus: 'none',
    // 法的同意
    agreedToPrivacyPolicy: true,
    agreedToTermsOfService: true,
    // 個人データの外国（越境）移転への同意（個人情報保護法28条）
    agreedToCrossBorderTransfer: true,
    legalAgreementTime: ts,
    // プラン
    selectedPlan: data.selectedPlan,
    // 登録元
    registrationSource: source,
  };

  // 紹介コード（検証済みの場合のみ）
  if (data.referralCode && data.referralValid) {
    doc.referralCode = data.referralCode;
  }

  // 端末申込
  if (data.wantsDevice) {
    const deviceApplication: Record<string, unknown> = {
      appliedAt: ts,
      status: 'pending',
      postalCode: data.postalCode,
      prefecture: data.prefecture,
      city: data.city.trim(),
      address: data.address.trim(),
      phoneNumber: data.phoneNumber,
      userRelation: data.userRelation,
    };

    if (data.building.trim()) {
      deviceApplication.building = data.building.trim();
    }

    if (data.userRelation === 'other' && data.userRelationOther.trim()) {
      deviceApplication.userRelationOther = data.userRelationOther.trim();
    }

    // オプション
    deviceApplication.wantsWifiPack = data.wantsWifiPack;
    deviceApplication.wantsSetupSupport = data.wantsSetupSupport;

    doc.deviceApplication = deviceApplication;
  }

  // LP固有: 質問・ご要望
  if (data.question && data.question.trim()) {
    doc.lpQuestion = data.question.trim();
  }

  return doc;
}
